import React, { memo, useCallback, useState } from 'react';
import { Modal } from 'react-bootstrap';

import FeedbackForm from './FeedbackForm';
import './FeedbackModal.css';

export interface FeedbackModalProps {
  show: boolean;
  onClose: () => void;
  /** Workflow ja definido pelo contexto (ex: ticket recem-finalizado). */
  workflowId?: number | null;
  /** Permite ou nao escolher 'Plataforma (geral)'. Padrao: true. */
  allowGeneralFeedback?: boolean;
  /** Permite ou nao trocar o workflow. Padrao: true. */
  allowWorkflowChange?: boolean;
  /** Titulo customizavel. */
  title?: string;
}

/**
 * Modal nao-intrusivo de feedback do usuario.
 *
 * - Backdrop padrao (clicar fora fecha).
 * - Centralizado, scrollavel, responsivo.
 * - Apos sucesso, mostra mensagem por 1.6s e fecha sozinho.
 * - O fluxo respeita o opt-out: o usuario pode simplesmente fechar.
 */
const FeedbackModal: React.FC<FeedbackModalProps> = memo(({
  show,
  onClose,
  workflowId = null,
  allowGeneralFeedback = true,
  allowWorkflowChange = true,
  title = 'Como foi sua experiência?',
}) => {
  const [submitted, setSubmitted] = useState<boolean>(false);

  const handleClose = useCallback(() => {
    setSubmitted(false);
    onClose();
  }, [onClose]);

  const handleSuccess = useCallback(() => {
    setSubmitted(true);
    // Fecha sozinho apos breve confirmacao visual.
    const timer = window.setTimeout(() => {
      handleClose();
    }, 1600);
    // Cleanup defensivo: se o modal for desmontado antes, evita callback solto.
    return () => window.clearTimeout(timer);
  }, [handleClose]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      backdrop={true}
      keyboard
      className="feedback-modal"
      aria-labelledby="feedback-modal-title"
    >
      <Modal.Header closeButton className="feedback-modal__header">
        <Modal.Title id="feedback-modal-title" className="feedback-modal__title">
          <i className="bi bi-chat-heart-fill me-2" aria-hidden="true"></i>
          {title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="feedback-modal__body">
        <FeedbackForm
          initialWorkflowId={workflowId}
          allowGeneralFeedback={allowGeneralFeedback}
          allowWorkflowChange={allowWorkflowChange}
          onCancel={submitted ? undefined : handleClose}
          onSuccess={handleSuccess}
          compact
        />
      </Modal.Body>
    </Modal>
  );
});

FeedbackModal.displayName = 'FeedbackModal';

export default FeedbackModal;
